import { useState } from 'react';
import { Link } from 'react-router';
import { planName } from '@/shared/config/plans';
import { ROUTES } from '@/shared/config/routes';
import { formatMonthDay } from '@/shared/lib/format';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { useCancelPlan } from '../hooks/use-cancel-plan';
import { useKeepPlan } from '../hooks/use-keep-plan';
import { planLines, planPrice, planStatus } from '../model/billing-status';
import type { WorkspaceData } from '../types';

type PlanSummaryCardProps = { workspace: WorkspaceData };

export function PlanSummaryCard({ workspace }: PlanSummaryCardProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { cancelPlan, isCancelling } = useCancelPlan();
  const { keepPlan, isKeeping } = useKeepPlan();
  const subscription = workspace.billing.subscription;
  const name = planName(workspace.plan);
  const status = planStatus(subscription);
  const isPaid = subscription !== null;
  const endsOn = subscription?.endsAt ? formatMonthDay(subscription.endsAt) : null;
  const periodEnd = subscription?.currentPeriodEndsAt
    ? formatMonthDay(subscription.currentPeriodEndsAt)
    : null;

  const confirmCancel = async () => {
    await cancelPlan();
    setIsConfirming(false);
  };

  return (
    <Card asChild>
      <section data-enter="" aria-label="Plan" className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
          <div className="flex shrink grow basis-95 flex-col gap-2.5">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="font-semibold text-plan-title">{name}</h2>
              <span className="font-medium tabular-nums">{planPrice(workspace.plan)}</span>
              <Pill tone={status.tone}>{status.text}</Pill>
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-muted">
              {planLines(workspace.plan).map((line) => (
                <li key={line}>{line}</li>
              ))}
              {subscription?.scheduledPlan ? (
                <li>
                  Moves to {planName(subscription.scheduledPlan)} on{' '}
                  {periodEnd ?? 'the next period'}
                </li>
              ) : null}
            </ul>
          </div>
          <div className="flex flex-wrap gap-2">
            {isPaid ? null : (
              <Button asChild variant="primary">
                <Link to={ROUTES.PLANS}>See plans</Link>
              </Button>
            )}
            {isPaid ? (
              <Button asChild>
                <Link to={ROUTES.PLANS}>Change plan</Link>
              </Button>
            ) : null}
            {isPaid && !endsOn && !isConfirming ? (
              <Button variant="ghost" onClick={() => setIsConfirming(true)}>
                Cancel plan
              </Button>
            ) : null}
            {endsOn ? (
              <Button variant="primary" isLoading={isKeeping} onClick={() => void keepPlan()}>
                Keep {name}
              </Button>
            ) : null}
          </div>
        </div>
        {isConfirming ? (
          <div role="alertdialog" aria-labelledby="cancel-h">
            <Banner
              tone="bad"
              id="cancel-h"
              title={`Cancel ${name}?`}
              action={
                <div className="flex gap-2">
                  <Button onClick={() => setIsConfirming(false)}>Keep the plan</Button>
                  <Button
                    variant="danger"
                    isLoading={isCancelling}
                    onClick={() => void confirmCancel()}
                  >
                    Cancel on {periodEnd ?? 'the period end'}
                  </Button>
                </div>
              }
            >
              It stays active until {periodEnd ?? 'the end of the period'}. After that, new sessions
              stop getting Pods. Running Pods finish their sessions.
            </Banner>
          </div>
        ) : null}
        {endsOn ? (
          <Banner tone="warn" title={`${name} ends on ${endsOn}.`}>
            No more charges after that. You can keep the plan any time before then.
          </Banner>
        ) : null}
      </section>
    </Card>
  );
}
